(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.Axiom && typeof window.Axiom.formatMoney === 'function') return window.Axiom.formatMoney(Number(cents) || 0);
    return (Number(cents || 0) / 100).toFixed(2);
  }

  function parseJson(element, fallback) {
    if (!element) return fallback;
    try { return JSON.parse(element.textContent); } catch (error) { return fallback; }
  }

  class BundleMultipleSelect extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerOut = this._onPointerOut.bind(this);
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('keydown', this._onKeyDown);
      this.addEventListener('pointermove', this._onPointerMove);
      this.addEventListener('pointerout', this._onPointerOut);
      this._initialize();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('keydown', this._onKeyDown);
      this.removeEventListener('pointermove', this._onPointerMove);
      this.removeEventListener('pointerout', this._onPointerOut);
      this.items.forEach((item) => {
        if (item._variantAbort) item._variantAbort.abort();
        const media = item.querySelector('[data-bundle-multiple-media]');
        if (media && media._pointerFrame) cancelAnimationFrame(media._pointerFrame);
      });
      this._ready = false;
    }

    _initialize() {
      this.items = Array.from(this.querySelectorAll('[data-bundle-multiple-item]:not(.is-placeholder)'));
      this.tiers = parseJson(this.querySelector('[data-bundle-multiple-tiers]'), [])
        .filter((tier) => Number(tier.quantity) > 0)
        .map((tier) => ({ quantity: Number(tier.quantity), discount: Math.max(0, Number(tier.discount) || 0) }))
        .sort((a, b) => a.quantity - b.quantity || a.discount - b.discount);
      this.items.forEach((item) => this._prepareItem(item));
      this._render();
    }

    _prepareItem(item) {
      item._variants = parseJson(item.querySelector('[data-bundle-multiple-variants]'), []);
      item._variantState = window.AxiomHighVariantPicker && window.AxiomHighVariantPicker.read(item);
      item._highVariant = Boolean(window.AxiomHighVariantPicker && window.AxiomHighVariantPicker.isHighVariant(item._variantState));
      const id = String(item.dataset.variantId || '');
      item._variant = item._variants.find((variant) => String(variant.id) === id) || (item._variantState && item._variantState.variant) || null;
      item._selected = false;
      this._prepareMedia(item);
      this._syncVariantControls(item);
    }

    _prepareMedia(item) {
      const media = item.querySelector('[data-bundle-multiple-media]');
      if (!media) return;
      if (media.dataset.hoverEffect === 'media_gallery') this._setMediaIndex(media, 0);
    }

    _setMediaIndex(media, index) {
      if (!media) return;
      const slides = Array.from(media.querySelectorAll('[data-bundle-multiple-media-slide]'));
      if (!slides.length) return;
      const normalized = ((Number(index) || 0) % slides.length + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.style.transform = `translate3d(${(slideIndex - normalized) * 100}%, 0, 0)`;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      media.querySelectorAll('[data-bundle-multiple-media-dot]').forEach((dot, dotIndex) => {
        const active = dotIndex === normalized;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    _onPointerMove(event) {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (event.target.closest('[data-bundle-multiple-media-dot]')) return;
      const media = event.target.closest('[data-bundle-multiple-media]');
      if (!media || !this.contains(media) || media.dataset.hoverEffect !== 'media_gallery') return;
      const slides = media.querySelectorAll('[data-bundle-multiple-media-slide]');
      if (slides.length < 2) return;
      media._pointerX = event.clientX;
      if (media._pointerFrame) return;
      media._pointerFrame = requestAnimationFrame(() => {
        media._pointerFrame = null;
        const rect = media.getBoundingClientRect();
        if (!rect.width) return;
        const position = Math.max(0, Math.min(1, (media._pointerX - rect.left) / rect.width));
        let targetIndex = 0;
        if (slides.length >= 3) {
          if (position >= 1 / 3 && position < 2 / 3) targetIndex = 1;
          else if (position >= 2 / 3) targetIndex = 2;
        } else if (position >= .5) {
          targetIndex = 1;
        }
        this._setMediaIndex(media, targetIndex);
      });
    }

    _onPointerOut(event) {
      const media = event.target.closest('[data-bundle-multiple-media]');
      if (!media || !this.contains(media) || media.dataset.hoverEffect !== 'media_gallery' || (event.relatedTarget && media.contains(event.relatedTarget))) return;
      if (media._pointerFrame) {
        cancelAnimationFrame(media._pointerFrame);
        media._pointerFrame = null;
      }
      this._setMediaIndex(media, 0);
    }

    _selectedItems() {
      return this.items.filter((item) => item._selected && item._variant && item._variant.available);
    }

    _selectedValues(item) {
      return Array.from(item.querySelectorAll('[data-bundle-multiple-option]')).map((select) => select.value);
    }

    _selectedValueIds(item) {
      return Array.from(item.querySelectorAll('[data-bundle-multiple-option]')).map((select) => {
        const option = select.options[select.selectedIndex];
        return option && option.dataset.optionValueId;
      }).filter(Boolean);
    }

    _resolveLocalVariant(item) {
      const values = this._selectedValues(item);
      return item._variants.find((variant) => (variant.options || []).every((value, index) => value === values[index])) || null;
    }

    _onChange(event) {
      const select = event.target.closest('[data-bundle-multiple-option]');
      if (!select) return;
      const item = select.closest('[data-bundle-multiple-item]');
      if (!item) return;
      if (item._highVariant) this._requestVariant(item);
      else this._applyVariant(item, this._resolveLocalVariant(item));
    }

    _requestVariant(item) {
      const optionValueIds = this._selectedValueIds(item);
      if (!window.AxiomHighVariantPicker || optionValueIds.length !== item.querySelectorAll('[data-bundle-multiple-option]').length) return;
      if (item._variantAbort) item._variantAbort.abort();
      const controller = new AbortController();
      item._variantAbort = controller;
      item._variantLoading = true;
      item.classList.add('is-loading');
      this._render();
      window.AxiomHighVariantPicker.request(item.dataset.productUrl, optionValueIds, controller.signal)
        .then((state) => {
          item._variantState = state;
          this._applyVariant(item, state && state.variant);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') this._announce(this.dataset.variantError);
        })
        .finally(() => {
          if (item._variantAbort !== controller) return;
          item._variantLoading = false;
          item.classList.remove('is-loading');
          this._render();
        });
    }

    _applyVariant(item, variant) {
      item._variant = variant || null;
      const available = Boolean(variant && variant.available);
      item.classList.toggle('is-unavailable', !available);
      item.dataset.variantId = variant ? variant.id : '';
      item.dataset.variantPrice = variant ? variant.price : 0;
      const button = item.querySelector('[data-bundle-multiple-toggle]');
      if (button) button.disabled = !available;
      if (!available && item._selected) item._selected = false;

      const price = item.querySelector('[data-bundle-multiple-current-price]');
      if (price) price.textContent = formatMoney(variant ? variant.price : 0);
      const compare = item.querySelector('[data-bundle-multiple-compare-price]');
      if (compare) {
        compare.hidden = !(variant && Number(variant.compare_at_price) > Number(variant.price));
        compare.textContent = variant ? formatMoney(variant.compare_at_price) : '';
      }

      const imageData = variant && (variant.featured_image || variant.featuredImage);
      const imageUrl = imageData && (imageData.src || imageData.url);
      const media = item.querySelector('[data-bundle-multiple-media]');
      const image = item.querySelector('[data-bundle-multiple-media-slide][data-media-index="0"] .bundle-multiple-card__image');
      if (imageUrl && image && image.tagName === 'IMG') {
        image.src = imageUrl;
        image.removeAttribute('srcset');
        item.dataset.productImage = imageUrl;
      }
      if (media && media.dataset.hoverEffect === 'media_gallery') {
        this._setMediaIndex(media, 0);
      }
      this._syncVariantControls(item);
      this._syncItem(item);
      this._render();
    }

    _syncVariantControls(item) {
      const selectedValues = this._selectedValues(item);
      const highVariantOptions = new Map();
      if (item._highVariant && item._variantState) {
        (item._variantState.options || []).forEach((option) => {
          (option.values || []).forEach((value) => {
            highVariantOptions.set(String(option.position) + ':' + String(value.id), Boolean(value.available));
          });
        });
      }

      item.querySelectorAll('[data-bundle-multiple-option-group]').forEach((group) => {
        const select = group.querySelector('[data-bundle-multiple-option]');
        if (!select) return;
        const position = Number(select.dataset.optionPosition);
        const selectedOption = select.options[select.selectedIndex];
        const selectedId = selectedOption && selectedOption.dataset.optionValueId;
        const selectedLabel = group.querySelector('[data-bundle-multiple-selected-value]');
        if (selectedLabel) selectedLabel.textContent = select.value;

        group.querySelectorAll('[data-bundle-multiple-option-value]').forEach((control) => {
          const active = String(control.dataset.optionValueId) === String(selectedId);
          let available;
          if (item._highVariant) {
            available = highVariantOptions.get(String(position) + ':' + String(control.dataset.optionValueId));
          } else {
            const candidate = selectedValues.slice();
            candidate[position - 1] = control.dataset.optionValue;
            available = item._variants.some((variant) => variant.available && (variant.options || []).every((value, index) => value === candidate[index]));
          }
          control.classList.toggle('active', active);
          control.classList.toggle('unavailable', available === false);
          control.setAttribute('aria-pressed', active ? 'true' : 'false');
          control.dataset.optionValueAvailable = available === false ? 'false' : 'true';
        });
      });
    }

    _onClick(event) {
      const disclosure = event.target.closest('[data-bundle-multiple-disclosure]');
      if (disclosure) {
        event.preventDefault();
        const expanded = this.classList.toggle('is-summary-expanded');
        disclosure.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        return;
      }

      const mediaDot = event.target.closest('[data-bundle-multiple-media-dot]');
      if (mediaDot) {
        event.preventDefault();
        const media = mediaDot.closest('[data-bundle-multiple-media]');
        this._setMediaIndex(media, Number(mediaDot.dataset.mediaIndex));
        return;
      }

      const optionControl = event.target.closest('[data-bundle-multiple-option-value]');
      if (optionControl) {
        event.preventDefault();
        const group = optionControl.closest('[data-bundle-multiple-option-group]');
        const select = group && group.querySelector('[data-bundle-multiple-option]');
        if (!select) return;
        const target = Array.from(select.options).find((option) => String(option.dataset.optionValueId) === String(optionControl.dataset.optionValueId));
        if (!target || target.selected) return;
        select.value = target.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const toggle = event.target.closest('[data-bundle-multiple-toggle]');
      if (toggle) {
        event.preventDefault();
        const item = toggle.closest('[data-bundle-multiple-item]');
        this._toggleItem(item);
        return;
      }

      const remove = event.target.closest('[data-bundle-multiple-remove]');
      if (remove) {
        event.preventDefault();
        const item = this.items.find((entry) => entry.dataset.variantId === remove.dataset.bundleMultipleRemove);
        if (item) {
          item._selected = false;
          this._syncItem(item);
          this._render();
        }
        return;
      }

      const submit = event.target.closest('[data-bundle-multiple-submit]');
      if (submit && !submit.disabled) {
        event.preventDefault();
        this._submit(submit);
        return;
      }

      const item = event.target.closest('[data-bundle-multiple-item]');
      const interactive = event.target.closest('button, a, input, select, textarea, label, [data-bundle-multiple-option-group]');
      if (item && !interactive) {
        event.preventDefault();
        this._toggleItem(item);
      }
    }

    _onKeyDown(event) {
      if (event.key !== 'Escape' || !this.classList.contains('is-summary-expanded')) return;
      const disclosure = this.querySelector('[data-bundle-multiple-disclosure]');
      this.classList.remove('is-summary-expanded');
      if (disclosure) {
        disclosure.setAttribute('aria-expanded', 'false');
        disclosure.focus();
      }
    }

    _toggleItem(item) {
      if (!item || !this.items.includes(item) || item._variantLoading) return;
      const button = item.querySelector('[data-bundle-multiple-toggle]');
      if (!button || button.disabled || !item._variant || !item._variant.available) return;
      item._selected = !item._selected;
      this._syncItem(item);
      if (item._selected) this._animateToSummary(item);
      this._render();
    }

    _syncItem(item) {
      item.classList.toggle('is-selected', item._selected);
      const button = item.querySelector('[data-bundle-multiple-toggle]');
      if (button) {
        const action = item._selected ? this.dataset.removeItemLabel : this.dataset.addItemLabel;
        button.setAttribute('aria-pressed', item._selected ? 'true' : 'false');
        button.setAttribute('aria-label', `${action || (item._selected ? 'Remove from bundle' : 'Add to bundle')}: ${item.dataset.productTitle || ''}`);
      }
    }

    _activeTier(count) {
      return this.tiers.reduce((active, tier) => count >= tier.quantity ? tier : active, null);
    }

    _nextTier(count) {
      return this.tiers.find((tier) => count < tier.quantity) || null;
    }

    _statusText(count, active, next) {
      if (next) {
        const remaining = Math.max(0, next.quantity - count);
        const template = active ? this.dataset.nextTierMessage : this.dataset.firstTierMessage;
        return String(template || '')
          .split('[count]').join(String(remaining))
          .split('[save]').join(String(active ? active.discount : next.discount))
          .split('[next_save]').join(String(next.discount));
      }
      if (active) return String(this.dataset.bestTierMessage || '').split('[save]').join(String(active.discount));
      return this.dataset.emptyMessage || '';
    }

    _render() {
      const selected = this._selectedItems();
      const count = selected.length;
      const active = this._activeTier(count);
      const next = this._nextTier(count);
      const subtotal = selected.reduce((sum, item) => sum + Number(item._variant.price || 0), 0);
      const savings = active ? Math.round(subtotal * active.discount / 100) : 0;
      const total = subtotal - savings;
      const firstTier = this.tiers[0];
      const topTier = this.tiers[this.tiers.length - 1];
      const progressTarget = next ? next.quantity : (topTier ? topTier.quantity : 1);
      const progress = Math.min(100, Math.max(0, count / Math.max(1, progressTarget) * 100));

      const statusText = this._statusText(count, active, next);
      this._setText('[data-bundle-multiple-status]', statusText);
      this._setText('[data-bundle-multiple-mobile-total]', formatMoney(total));
      this._setText('[data-bundle-multiple-subtotal]', formatMoney(subtotal));
      this._setText('[data-bundle-multiple-savings]', '−' + formatMoney(savings));
      this._setText('[data-bundle-multiple-total]', formatMoney(total));
      const progressBar = this.querySelector('[data-bundle-multiple-progress]');
      if (progressBar) progressBar.style.setProperty('--bundle-progress', progress + '%');
      const currentDiscount = active ? active.discount : 0;
      const tierIncrease = next ? Math.max(0, next.discount - currentDiscount) : 0;
      const tierUpgrade = this.querySelector('[data-bundle-multiple-tier-upgrade]');
      if (tierUpgrade) tierUpgrade.hidden = !active || !next || tierIncrease <= 0;
      this._setText('[data-bundle-multiple-tier-increase]', Number.isInteger(tierIncrease) ? String(tierIncrease) : tierIncrease.toFixed(2).replace(/\.?0+$/, ''));
      const savingsRow = this.querySelector('[data-bundle-multiple-savings-row]');
      if (savingsRow) savingsRow.hidden = !savings;
      const empty = this.querySelector('[data-bundle-multiple-empty]');
      if (empty) empty.hidden = count > 0;
      this._renderSelectedList(selected);
      const missingSlots = Math.max(0, 3 - count);
      const skeletons = this.querySelector('[data-bundle-multiple-skeletons]');
      if (skeletons) skeletons.hidden = missingSlots === 0;
      this.querySelectorAll('[data-bundle-multiple-skeleton]').forEach((skeleton, index) => {
        skeleton.hidden = index >= missingSlots;
      });

      const selectedVariantLoading = selected.some((item) => item._variantLoading);
      this.querySelectorAll('[data-bundle-multiple-submit]').forEach((button) => {
        button.disabled = !firstTier || count < firstTier.quantity || this._submitting || selectedVariantLoading;
      });
    }

    _renderSelectedList(selected) {
      const list = this.querySelector('[data-bundle-multiple-selected-list]');
      if (!list) return;
      list.replaceChildren();
      selected.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'bundle-multiple-summary__item';
        const imageUrl = item.dataset.productImage;
        if (imageUrl) {
          const image = document.createElement('img');
          image.className = 'bundle-multiple-summary__item-image';
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          row.appendChild(image);
        } else {
          const media = document.createElement('span');
          media.className = 'bundle-multiple-summary__item-image';
          row.appendChild(media);
        }
        const content = document.createElement('div');
        const title = document.createElement('p');
        title.className = 'bundle-multiple-summary__item-title';
        title.textContent = item.dataset.productTitle;
        const variant = document.createElement('p');
        variant.className = 'bundle-multiple-summary__item-variant type-style type-style--caption';
        variant.textContent = this._selectedValues(item).join(' / ');
        const price = document.createElement('p');
        price.className = 'bundle-multiple-summary__item-price';
        price.textContent = formatMoney(item._variant.price);
        content.appendChild(title);
        if (variant.textContent) content.appendChild(variant);
        content.appendChild(price);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'bundle-multiple-summary__remove';
        remove.dataset.bundleMultipleRemove = item.dataset.variantId;
        remove.setAttribute('aria-label', (this.dataset.removeItemAria || 'Remove [product]').split('[product]').join(item.dataset.productTitle));
        remove.textContent = '×';
        row.append(content, remove);
        list.appendChild(row);
      });
    }

    _animateToSummary(item) {
      if (this.dataset.animationEnabled !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const source = item.querySelector('[data-bundle-multiple-media-slide].is-active .bundle-multiple-card__image') || item.querySelector('.bundle-multiple-card__image');
      const target = this.querySelector('[data-bundle-multiple-summary]');
      if (!source || source.tagName !== 'IMG' || !target || typeof source.animate !== 'function') return;
      const from = source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      const clone = source.cloneNode();
      clone.className = 'bundle-multiple-fly';
      const sourceShadow = getComputedStyle(item).getPropertyValue('--shadow-soft').trim();
      if (sourceShadow) clone.style.setProperty('--bundle-fly-shadow', sourceShadow);
      Object.assign(clone.style, { left: from.left + 'px', top: from.top + 'px', width: from.width + 'px', height: from.height + 'px' });
      document.body.appendChild(clone);
      const animation = clone.animate([
        { transform: 'translate3d(0,0,0) scale(1)', opacity: .85 },
        { transform: `translate3d(${to.left + to.width / 2 - from.left - from.width / 2}px, ${to.top + 40 - from.top}px, 0) scale(.15)`, opacity: 0 }
      ], { duration: 520, easing: 'cubic-bezier(.2,.7,.2,1)' });
      animation.finished.catch(() => {}).finally(() => clone.remove());
    }

    _submit(button) {
      const selected = this._selectedItems();
      const payload = selected.map((item) => ({ id: item._variant.id, quantity: 1 }));
      if (!payload.length || !window.AxiomStandardStorefront || typeof window.AxiomStandardStorefront.addLines !== 'function') return;
      if (window.AxiomAddToCartAnimation) {
        const animationItems = selected.map((item) => ({
          source: item.querySelector('[data-bundle-multiple-media-slide].is-active .bundle-multiple-card__image') || item.querySelector('.bundle-multiple-card__image') || button,
          imageUrl: item.dataset.productImage || '',
          quantity: 1
        }));
        window.AxiomAddToCartAnimation.play({ button: button, items: animationItems, mode: 'main' });
      }
      this._submitting = true;
      this._render();
      button.classList.add('loading');
      this._announce(this.dataset.addingMessage);
      window.AxiomStandardStorefront.addLines(payload, { target: this, source: 'bundle-multiple-select' })
        .catch(() => this._announce(this.dataset.addError))
        .finally(() => {
          this._submitting = false;
          button.classList.remove('loading');
          this._render();
        });
    }

    _setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    }

    _announce(message) {
      const live = this.querySelector('[data-bundle-multiple-live]');
      if (live) live.textContent = message || '';
    }
  }

  if (!customElements.get('bundle-multiple-select')) customElements.define('bundle-multiple-select', BundleMultipleSelect);
})();
